import React, { useEffect, useState, useCallback, Fragment } from "react";
import { withTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import HeaderVialma from "components/HeaderVialma";
import VoucherBox from "./VoucherBox";
import Axios from "axios";
import { Mixpanel } from "utils/mixpanel";
import notificationManager from "utils/notificationManager";
import LoadingBubble from "components/LoadingBubble";
import WhyChooseVialma from "components/WhyChooseVialma";
import { withRouter } from "react-router";
import validateEmail from "utils/validateEmail";
import VoucherActivationForm from "./VoucherActivationForm";
import useLogout from "utils/hooks/useLogout";
import { dispatchUpdateAccount } from "modules/Account/actions";
import { dispatchLogin } from "modules/Config/actions";
import * as Sentry from "@sentry/react";
import VoucherHero from "./VoucherHero";
import { encodeWithQuote } from "utils/tools";
import { apiLibraryGetClient } from "utils/apiLibrary/client";

const VoucherActivation = ({ t, match }) => {
  const lang = useSelector((state) => state.config.lang);
  const [isLoading, setIsLoading] = useState(true);
  const [isActivationInProgress, setIsActivationInProgress] = useState(false);
  const voucherCode = match.params.voucherCode;
  const defaultMessage = t("VOUCHER_ACTIVATE_DEFAULT_MESSAGE");
  const [error, setError] = useState("");
  const dispatch = useDispatch();
  const logout = useLogout();

  // Details retrieved from voucher code
  const [gifteeEmail, setGifteeEmail] = useState("");
  const [gifterName, setGifterName] = useState("");
  const [imageRootUrl, setImageRootUrl] = useState("");
  const [imagePath, setImagePath] = useState("");
  const [message, setMessage] = useState("");
  const [giftcardName, setGiftcardName] = useState("");

  const getVoucherDetails = useCallback(
    (code) => {
      const url = `${process.env.REACT_APP_ENSEMBLE_API}/crm/vouchers/${code}?lang=${lang}`;
      setIsLoading(true);

      Axios({
        method: "get",
        url: url,
      })
        .then((response) => {
          if (response.data) {
            setGifteeEmail(response.data.giftee_email);
            setGifterName(response.data.gifter_name);
            setImageRootUrl(response.data.imageRootUrl);
            setImagePath(response.data.imagePath);
            setMessage(response.data.message);
            setGiftcardName(response.data.giftcard_title);
          }
        })
        .catch(() => {
          notificationManager.showMessage("none", t("VOUCHER_ACTIVATE_CODE_WRONG_NOTIFICATION"));
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    // eslint-disable-next-line
    [lang]
  );

  useEffect(() => {
    getVoucherDetails(voucherCode);
  }, [getVoucherDetails, voucherCode]);

  const validateForm = (email, password) => {
    if (!validateEmail(email)) {
      setError(t("VOUCHER_VALIDATION_ERROR_EMAIL"));
      return false;
    }
    if (password.length <= 5) {
      setError(t("VOUCHER_VALIDATION_ERROR_PASSWORD"));
      return false;
    }
    setError("");
    return true;
  };

  const login = (email, password, redirectUrl) => {
    const urlLogin = `${process.env.REACT_APP_VIALMA_API}/login_check?lang=${lang}`;
    const payload = { _username: encodeWithQuote(email), _password: encodeWithQuote(password) };
    Axios.post(urlLogin, payload).then(async (res) => {
      // I hate this:
      if (["Sorry! The user name and password do not match.", "Désolé ! L'email ou le mot de passe est incorrect."].includes(res.data[0])) {
        setIsActivationInProgress(false);
        notificationManager.showMessage("none", t("VOUCHER_ACTIVATE_ACCOUNT_EXISTS"));
        return;
      }
      if (res.data.token) {
        const responseAccount = await apiLibraryGetClient(`Bearer ${res.data.token}`);
        const subscription = responseAccount.subscription_ensemble.subscription;
        const subscriptionEndDate = responseAccount?.subscription_ensemble?.endDate?.date;

        localStorage.setItem("loginData", JSON.stringify(Object.assign({}, res.data, responseAccount)));
        Mixpanel.identify(responseAccount.subscription_ensemble.user.id);
        Mixpanel.people.set_once({
          "First Login Date": new Date().toISOString(),
          $email: responseAccount.email,
          $name: res.data.name,
          "User ID": responseAccount.subscription_ensemble.user.id,
          "On Trial": subscription.trial,
          "Subscription Plan": subscription.name,
          "Subscription Interval": subscription.unit,
          "Subscription Id": subscription.subscriptionId,
          "Subscription End Date": subscriptionEndDate ? new Date(subscriptionEndDate.substring(0, 19).replace(/-/g, "/")).toISOString() : "none",
        });
        Mixpanel.register({ "Subscription Plan": subscription.name });
        Mixpanel.track("Logged In", {
          "Subscription Interval": subscription.unit,
          "Subscription End Date": subscriptionEndDate ? new Date(subscriptionEndDate.substring(0, 19).replace(/-/g, "/")).toISOString() : "none",
        });
        Sentry.setUser({ email: responseAccount.email, plan: subscription.name, userId: responseAccount.subscription_ensemble.user.id });
        dispatch(dispatchUpdateAccount(responseAccount));
        dispatch(dispatchLogin(true));
        window.top.location = redirectUrl;
      }
    });
  };

  const activateVoucher = (email, password) => {
    if (!validateForm(email, password)) return;

    setIsActivationInProgress(true);
    const url = `${process.env.REACT_APP_ENSEMBLE_API}/crm/activate/voucher/${voucherCode}?lang=${lang}`;
    const formData = new FormData();
    formData.append("email", email);
    formData.append("password", password);

    Axios({
      method: "post",
      url: url,
      data: formData,
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
    })
      .then((response) => {
        if (response.data) {
          Mixpanel.track("Giftcard redeemed", { user: response.data.user });
          if (response.data.customer && response.data.customer.id) {
            logout(); // need to log out first
            login(email, password, response.data.redirectUrl);
          }
        }
      })
      .catch((response) => {
        setIsActivationInProgress(false);
        notificationManager.showMessage("none", t("VOUCHER_ACTIVATE_FAILURE"));
      });
  };

  return (
    <Fragment>
      <div className="voucher-activation">
        <HeaderVialma noUniverse noSubscribe />
        <VoucherHero text={[t("VOUCHER_BANNER_PART1"), ""]} />
        {isLoading ? (
          <div className="gift-loading-icon">
            <LoadingBubble color={"#DD4E52"} />
          </div>
        ) : (
          <div className="voucher-activation__content">
            <VoucherBox imageRootUrl={imageRootUrl} imagePath={imagePath} giftcardName={giftcardName} />
            <div className={`voucher-form`}>
              <h1 className="voucher-form__title-1">{t("VOUCHER_FORM_TITLE1")}</h1>
              <div className="voucher-form__wrapper">
                <VoucherActivationForm
                  defaultEmail={gifteeEmail}
                  activateFunc={activateVoucher}
                  loading={isActivationInProgress}
                ></VoucherActivationForm>
                <p>{error}</p>
              </div>
              <h1 className="voucher-form__title-2">{t("VOUCHER_LETTER_TITLE", gifterName)}</h1>
              <p>{message || defaultMessage} </p>
            </div>
          </div>
        )}
      </div>
      <WhyChooseVialma headText={t("ABOUT_VIALMA_4_TITLE")} />
    </Fragment>
  );
};

export default withRouter(withTranslation()(VoucherActivation));
